import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useDeleteAccount } from '@/data/account';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { SettingsNav } from '@/components/settings/SettingsNav';
import { toast } from '@/lib/toast';

export const Route = createFileRoute('/parent/settings/account')({
  component: AccountSettings,
});

// The exact phrase the user must type before the destructive action unlocks.
const CONFIRM_PHRASE = 'DELETE';

function AccountSettings() {
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const deleteAccount = useDeleteAccount();

  const canDelete = confirmText.trim().toUpperCase() === CONFIRM_PHRASE && !deleteAccount.isPending;

  function closeDialog() {
    if (deleteAccount.isPending) return;
    setOpen(false);
    setConfirmText('');
  }

  async function handleDelete() {
    if (!canDelete) return;
    try {
      // On success the hook signs the user out and navigates to /login, so the
      // component unmounts before any further state update runs here.
      await deleteAccount.mutateAsync();
    } catch (err) {
      toast({
        title: "Couldn't delete your account",
        description: err instanceof Error ? err.message : 'Unexpected error. Please try again.',
        variant: 'error',
      });
    }
  }

  return (
    <div className="space-y-10">
      <header>
        <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
          Settings
        </p>
        <h1 className="mt-2">Account</h1>
        <p className="mt-2 max-w-prose text-[var(--color-ink-muted)]">
          Manage your Pirouette account.
        </p>
        <SettingsNav className="mt-4" />
      </header>

      <section
        aria-labelledby="danger-section"
        className="surface-elevated border border-[var(--color-danger)]/40 p-6"
      >
        <h2
          id="danger-section"
          className="font-medium text-[var(--color-danger)] text-[var(--text-lg)]"
        >
          Delete account
        </h2>
        <div className="mt-2 max-w-prose space-y-2 text-[var(--color-ink-muted)] text-[var(--text-sm)]">
          <p>
            Deleting your account is <strong>permanent and immediate</strong>. You will be signed
            out and will lose access to Pirouette. This cannot be undone.
          </p>
          <p>
            This removes your login and your role in your family. Some records the studio is legally
            required to keep — including children&rsquo;s consent records (COPPA) — are retained as
            required by law. Studio billing and operational records remain with the studio.
          </p>
        </div>

        <Dialog.Root
          open={open}
          onOpenChange={(o) => {
            if (o) {
              setOpen(true);
            } else {
              closeDialog();
            }
          }}
        >
          <Dialog.Trigger asChild>
            <Button variant="danger" className="mt-4">
              Delete account
            </Button>
          </Dialog.Trigger>
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 z-[var(--z-overlay)] bg-[var(--color-ink)]/30 backdrop-blur-sm" />
            <Dialog.Content className="surface-elevated fixed top-1/2 left-1/2 z-[var(--z-modal)] max-h-[80vh] w-[min(90vw,32rem)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto p-6">
              <Dialog.Title className="font-[var(--font-display)] text-[var(--color-danger)] text-[var(--text-2xl)]">
                Delete your account?
              </Dialog.Title>
              <Dialog.Description className="mt-2 text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                This is permanent and takes effect immediately. Your login and your role in your
                family will be removed and you&rsquo;ll be signed out. Legally-retained consent
                records (COPPA) are kept as required by law.
              </Dialog.Description>

              <div className="mt-6">
                <Label htmlFor="confirm-delete">
                  Type <span className="font-semibold">{CONFIRM_PHRASE}</span> to confirm
                </Label>
                <Input
                  id="confirm-delete"
                  type="text"
                  autoComplete="off"
                  autoCapitalize="characters"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder={CONFIRM_PHRASE}
                  disabled={deleteAccount.isPending}
                  aria-describedby="confirm-delete-help"
                />
                <p
                  id="confirm-delete-help"
                  className="mt-1.5 text-[var(--color-ink-subtle)] text-[var(--text-xs)]"
                >
                  This keeps an accidental tap from deleting your account.
                </p>
              </div>

              <div className="mt-6 flex justify-end gap-2">
                <Dialog.Close asChild>
                  <Button variant="secondary" disabled={deleteAccount.isPending}>
                    Cancel
                  </Button>
                </Dialog.Close>
                <Button variant="danger" onClick={() => void handleDelete()} disabled={!canDelete}>
                  {deleteAccount.isPending ? 'Deleting…' : 'Delete my account'}
                </Button>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      </section>
    </div>
  );
}
